import { useProvinceAnalytics } from "@/hooks/useProvinceAnalytics";
import { getCanonicalLocation } from "@/lib/canonicalLocation";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Component to automatically track Argentina province analytics
 * Should be included in the main App component
 * Only activates for users actually in Argentina
 */
export const ArgentinaProvinceAnalytics = () => {
  const [isArgentina, setIsArgentina] = useState(false);
  const location = useLocation();

  // Check if user is in Argentina before initializing analytics.
  // VERDAD ÚNICA: país del Worker (canonicalLocation) — el gate anterior por
  // timezone/idioma activaba AR analytics para cualquier reloj argentino
  // (incl. VPNs) y quedó prohibido por validate-location-single-truth.cjs.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsArgentina(getCanonicalLocation().country === "AR");
  }, []);

  // Only use province analytics if user is in Argentina
  const { isTracking, provinceAnalytics, trackInteraction } = useProvinceAnalytics();
  
  // Track route changes
  useEffect(() => {
    if (isTracking) {
      trackInteraction('page_navigation', {
        from_path: location.pathname,
        to_path: location.pathname,
        navigation_type: 'route_change'
      });
    }
  }, [location.pathname, isTracking, trackInteraction]);

  // Track session start
  useEffect(() => {
    if (isTracking) {
      trackInteraction('session_start', {
        entry_page: location.pathname,
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, [isTracking, trackInteraction]); // Only run once when tracking starts

  // Track page visibility changes
  useEffect(() => {
    if (!isTracking) return;

    const handleVisibilityChange = () => {
      trackInteraction('page_visibility_change', {
        visibility_state: document.visibilityState,
        page_path: location.pathname
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isTracking, trackInteraction, location.pathname]);

  // Track beforeunload (session end)
  useEffect(() => {
    if (!isTracking) return;

    const handleBeforeUnload = () => {
      trackInteraction('session_end', {
        exit_page: location.pathname,
        session_duration: provinceAnalytics.sessionMetrics.sessionDuration,
        total_interactions: provinceAnalytics.sessionMetrics.interactions,
        total_cta_clicks: provinceAnalytics.sessionMetrics.ctaClicks,
        max_scroll_depth: provinceAnalytics.sessionMetrics.maxScrollDepth
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isTracking, trackInteraction, location.pathname, provinceAnalytics]);

  // Don't render anything if not in Argentina - AFTER all hooks are called
  if (!isArgentina) {
    return null;
  }

  // This component doesn't render anything
  return null;
};

/**
 * Enhanced tracking component for specific interactions
 */
export const ProvinceTrackingWrapper: React.FC<{
  children: React.ReactNode;
  trackingName: string;
  trackingData?: Record<string, any>;
}> = ({ children, trackingName, trackingData = {} }) => {
  const { trackInteraction, isTracking } = useProvinceAnalytics();

  const handleClick = (event: React.MouseEvent) => {
    if (isTracking) {
      trackInteraction('element_click', {
        element_name: trackingName,
        element_type: (event.target as HTMLElement)?.tagName?.toLowerCase(),
        click_coordinates: { x: event.clientX, y: event.clientY },
        ...trackingData
      });
    }
  };

  const handleMouseEnter = () => {
    if (isTracking) {
      trackInteraction('element_hover', {
        element_name: trackingName,
        ...trackingData
      });
    }
  };

  return (
    <div onClick={handleClick} onMouseEnter={handleMouseEnter}>
      {children}
    </div>
  );
};